import { ActionIcon, Button, Group, Menu, Text, VisuallyHidden } from "@mantine/core";
import { Check, Minimize, Pause, Play } from "lucide-react";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from "react";

import { textClipTextMax } from "../../api/generated/zod";
import { mediaCrossOrigin } from "../../api/media-cookie";
import { readPref, writePref } from "../../platform/prefs";
import { CanvasHandles } from "./CanvasHandles";
import { CanvasTools, type CanvasCommand } from "./CanvasTools";
import {
  FIT_VIEW,
  fitScale,
  panned,
  stageScale,
  steppedZoom,
  zoomed,
  type CanvasView,
  type CanvasZoom,
} from "./canvas-view";
import {
  GROUP_HANDLES,
  SNAP_SCREEN_PX,
  boundsOf,
  dragOutcome,
  handlesOf,
  jointOf,
  placeOf,
  type DragState,
  type Gesture,
  type Point,
  type Rect,
} from "./canvas-drag";
import handleClasses from "./canvas-handles.module.css";
import { boostOf, setBoost } from "./engine/boost";
import {
  FIRST_PACE,
  REST_DRIFT_MS,
  layerWant,
  moveToward,
  nextPace,
  soundWant,
  type MediaMove,
  type StartPace,
} from "./engine/follow";
import { loadAgain } from "./engine/load-again";
import { facesOf, liveFrame, resolveProject, type EngineProject, type Size } from "./engine/project";
import { ScrubCache, type ScrubAsk } from "./engine/scrub";
import { layersAhead, layersAt, soundsAt, textsAt } from "./engine/schedule";
import { faceName, faceReady, loadFaces } from "./fonts";
import { pick, sameDrawn, shareOf, toFrame, type Drawn } from "./engine/pick";
import type { Picture, Pictures, Scene } from "./engine/scene";
import { engineSupported } from "./engine/support";
import { cssRatio, type AspectRatio } from "./project";
import type { SourceMedia } from "./media";
import { QualityPill } from "./QualityPill";
import { playedMedia, qualityWords, type PreviewQuality } from "./quality";
import { LINE_PITCH, paddingOf } from "./text-style";
import { formatTimecode } from "./time";
import type { Selection } from "./selection";
import type { MenuPlace } from "./TimelineMenu";
import { editTimeline, type TextClip, type Timeline, type TimelineEdit } from "./timeline";
import { useCanvasTouch } from "./use-canvas-touch";
import classes from "./editor.module.css";

export interface PreviewProps {
  timeline: Timeline;
  /** The chosen clips: each drawn now has live's selection border and corners round it. */
  selection: Selection | null;
  /** A press on the frame picks the clip drawn on top there, or none. */
  onPick: (clipId: string | null, more: boolean) => void;
  /** The member may not change the project: a text's editor does not open. */
  readOnly: boolean;
  /** A change made on the canvas: a text's words (its editor), a move by the arrow keys. */
  onEdit: (edit: TimelineEdit) => void;
  /** A right-click on the frame: live's canvas menu, on the clip drawn on top there or none. */
  onMenu: (place: MenuPlace) => void;
  aspectRatio: AspectRatio;
  /** Live's ratio menu: a change of the frame's shape; none while the member may not change it. */
  onAspect: ((aspect: AspectRatio) => void) | undefined;
  media: ReadonlyMap<string, SourceMedia>;
  playheadMs: number;
  playing: boolean;
  onPlayhead: (ms: number) => void;
  onPlaying: (playing: boolean) => void;
  /** The preview axis's time (spec capcut-shortcuts.md): paused, the canvas shows it, not the playhead's. */
  hoverMs: number | null;
  /** Whether the machine names its command key ⌘: the zoom menu's keys. */
  mac: boolean;
  /** Where the editor's keys reach the canvas's view: its tools, its zoom and full screen. */
  controls: RefObject<CanvasControls | null>;
  /** A phone's: a long-press on the frame opens its menu (spec phone-canvas.md). */
  touch?: boolean | undefined;
}

/** The canvas's view, as the editor's keys ask for it. */
export interface CanvasControls {
  run: (command: CanvasCommand) => void;
}

/** Whether this browser can put an element full screen (jsdom and some phones cannot). */
export const fullScreenSupported = () =>
  typeof HTMLElement !== "undefined" && typeof HTMLElement.prototype.requestFullscreen === "function";


const CORNERS = ["topLeft", "topRight", "bottomLeft", "bottomRight"] as const;
const CORNER_PLACE: Record<(typeof CORNERS)[number], CSSProperties> = {
  topLeft: { top: -4, left: -4 },
  topRight: { top: -4, right: -4 },
  bottomLeft: { bottom: -4, left: -4 },
  bottomRight: { bottom: -4, right: -4 },
};

/**
 * A drag on the canvas (issue 560) with what it needs while it runs: the frame in live's pixels,
 * the other items' bounds it snaps to, the snap's reach in live's pixels (5 of the screen's), and
 * whether a body drag has gone far enough to move (3 of the screen's pixels; a handle's at once).
 */
interface CanvasDrag extends DragState {
  frame: { width: number; height: number };
  others: readonly Rect[];
  threshold: number;
  /** The screen's pixels to one of live's. */
  scale: number;
  moving: boolean;
}

/** How far a press on an item goes before it is a move, in the screen's pixels. */
const MOVE_START_PX = 3;

/** No page text is selected while a drag sweeps across it: undone by the function returned. */
function holdSelection(): () => void {
  const selecting = document.body.style.userSelect;
  document.body.style.userSelect = "none";
  return () => {
    document.body.style.userSelect = selecting;
  };
}

/** How often, at most, the playhead follows the playing video. */
const PLAYHEAD_STEP_MS = 50;

/** How far past the playhead the pictures to come are loaded, paused at their first frames. */
const AHEAD_MS = 2000;

/** The frame's width over its height, for the CSS that fits it in the canvas. */
function ratioOf(aspect: AspectRatio): number {
  const [width = 1, height = 1] = aspect.split(":").map(Number);
  return width / height;
}

/** Live's ratios (`canvas-panel.tsx`), in its menu's order, with its words and its shapes: the
 * pill's 16px one and the menu's 20px one. */
const RATIOS = [
  { ratio: "16:9", use: "YouTube ads", pill: [16, 9], item: [20, 11] },
  { ratio: "9:16", use: "TikTok, TikTok ads...", pill: [9, 16], item: [11, 20] },
  { ratio: "1:1", use: "Instagram feed", pill: [14, 14], item: [16, 16] },
  { ratio: "4:5", use: "Instagram, Facebook feed", pill: [11, 14], item: [13, 16] },
  { ratio: "4:3", use: "Classic TV, iPad", pill: [16, 12], item: [16, 12] },
  { ratio: "21:9", use: "Ultrawide, cinematic", pill: [20, 10], item: [20, 10] },
] as const;

function Shape({ size, className }: { size: readonly [number, number]; className: string | undefined }) {
  return <span className={className} style={{ width: size[0], height: size[1] }} aria-hidden />;
}

/**
 * Live's resize control on the canvas (decisions 21 and 22): the project's ratio, and its menu of
 * live's six, a check on the current one. A choice is one undo step.
 */
function AspectControl(props: { aspectRatio: AspectRatio; onAspect: ((aspect: AspectRatio) => void) | undefined }) {
  const { aspectRatio, onAspect } = props;
  const current = RATIOS.find((each) => each.ratio === aspectRatio)!;
  return (
    <Menu position="bottom-start" width={256} disabled={onAspect === undefined}>
      <Menu.Target>
        <Button
          variant="default"
          className={classes.aspectButton}
          leftSection={<Shape size={current.pill} className={classes.aspectShape} />}
          aria-label={`Aspect ratio ${aspectRatio}`}
        >
          {aspectRatio}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {RATIOS.map(({ ratio, use, item }) => {
          const body = (
            <Group gap="md" wrap="nowrap">
              <Shape size={item} className={classes.aspectItemShape} />
              <span className={classes.aspectItemText}>
                <Text size="sm" fw={500}>
                  {ratio}
                </Text>
                <Text size="xs" c="var(--cai-color-text-muted)">
                  {use}
                </Text>
              </span>
              {/* Live's check on the current one, said to a screen reader too. */}
              {ratio === aspectRatio ? <VisuallyHidden>, current</VisuallyHidden> : null}
            </Group>
          );
          const check = ratio === aspectRatio ? <Check size={16} color="var(--cai-color-primary)" aria-hidden /> : null;
          return (
            <Menu.Item key={ratio} rightSection={check} onClick={() => onAspect?.(ratio)}>
              {body}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

/** Starts the element, which a test's jsdom or a blocked autoplay may refuse. */
function start(element: HTMLMediaElement): void {
  try {
    const played = element.play() as Promise<void> | undefined;
    played?.catch(() => undefined);
  } catch {
    // Refused: the picture stays; the clock still runs.
  }
}

/** The longest Play waits for its first new picture before the clock runs anyway (a stalled file). */
const MOST_FIRST_WAIT_MS = 300;

/**
 * A run of the play clock: each playing video's frame watch (asked for its every picture, so the
 * browser shows a hidden video's pictures as it plays), the starts not seen yet (when each video
 * was asked to play, and where it was), whether any start is watched, the first new picture seen
 * (when, and how far into its file), and whether the run is over, when its watches stop and its
 * starts teach nothing. Held weakly: a clip's video, gone, is let go with its file and decoder.
 */
interface ClockRun {
  watched: WeakMap<HTMLVideoElement, symbol>;
  starts: WeakMap<HTMLVideoElement, { at: number; fromMs: number }>;
  /** Each element's progress when it last made some, and when (spec stalled-element), and its reloads. */
  progressed: WeakMap<HTMLMediaElement, { progress: string; at: number }>;
  reloads: WeakMap<HTMLMediaElement, number>;
  watching: boolean;
  first: { at: number; aheadMs: number } | null;
  over: boolean;
}

/**
 * The frame asks waiting on each video. A video paused or gone has its ask taken back: one left
 * waiting holds the video, and with it its file's connection and decoder, long after its clip.
 */
function stopWatching(waiting: Map<HTMLVideoElement, number>, element: HTMLVideoElement): void {
  const ask = waiting.get(element);
  if (ask === undefined) return;
  element.cancelVideoFrameCallback(ask);
  waiting.delete(element);
}

/** Makes on an element the changes the play clock asked for at a frame (`engine/follow.ts`). */
function apply(element: HTMLMediaElement, move: MediaMove): void {
  if (move.reload) element.load();
  if (move.muted !== undefined) element.muted = move.muted;
  if (move.volume !== undefined) element.volume = move.volume;
  if (move.boost !== undefined) setBoost(element, move.boost);
  if (move.rate !== undefined) element.playbackRate = move.rate;
  if (move.seekMs !== undefined) element.currentTime = move.seekMs / 1000;
  if (move.pause) element.pause();
  if (move.play) start(element);
}

/**
 * A media element at the playhead while the preview is paused: there, at `wantedMs` of its
 * source, at the clip's level. While it plays (`clocked`), the play clock keeps it at every frame
 * instead (spec playback-trail): this leaves it alone. Returns the ref to put on the element.
 */
function useFollow<E extends HTMLMediaElement>(
  url: string | null,
  wantedMs: number,
  clocked: boolean,
  level: { muted: boolean; volume: number },
): RefObject<E | null> {
  const media = useRef<E>(null);
  useEffect(() => {
    const element = media.current;
    if (element === null || url === null || clocked) return;
    element.muted = level.muted;
    element.volume = Math.min(1, Math.max(0, level.volume));
  }, [media, url, clocked, level.muted, level.volume]);
  useEffect(() => {
    const element = media.current;
    if (element === null || url === null || clocked) return;
    if (!element.paused) element.pause();
    if (element.playbackRate !== 1) element.playbackRate = 1;
    if (Math.abs(element.currentTime * 1000 - wantedMs) > REST_DRIFT_MS) element.currentTime = wantedMs / 1000;
  }, [media, url, wantedMs, clocked]);
  return media;
}

type MediaElement = HTMLVideoElement | HTMLImageElement;

/**
 * What a media element shows now, once it has a picture; nothing while it loads or seeks, when
 * its track keeps its last picture (spec cut-preload). A file that failed has none to wait for:
 * size 0, so its layer is left out, as a broken still's is.
 */
function pictureOf(element: MediaElement): Picture | null {
  if (element instanceof HTMLVideoElement) {
    if (element.error) return { source: element, width: 0, height: 0, moving: false };
    // HAVE_CURRENT_DATA: the frame at its current time is decoded.
    if (element.readyState < 2) return null;
    return { source: element, width: element.videoWidth, height: element.videoHeight, moving: true };
  }
  if (!element.complete) return null;
  return { source: element, width: element.naturalWidth, height: element.naturalHeight, moving: false };
}

type EngineState = "starting" | "ready" | "unavailable";

/** The scene's chunk, and the code Pixi fetches on demand as a scene starts (`loadSceneCode`). */
async function loadEngine() {
  const engine = await import("./engine/scene");
  await engine.loadSceneCode();
  return engine;
}

/**
 * The engine's scene, made once its host is there (its code fetched on demand, and again after a
 * failed fetch, spec flaky-tests-1216.md), its canvas placed in the host, and destroyed with it:
 * its state ("unavailable" where the browser cannot run it) and a stable `draw`, which does
 * nothing until the scene is ready.
 */
function useScene(host: RefObject<HTMLDivElement | null>, size: Size) {
  const scene = useRef<Scene | null>(null);
  const [state, setState] = useState<EngineState>(engineSupported() ? "starting" : "unavailable");
  const first = useRef(size);
  useEffect(() => {
    const element = host.current;
    if (element === null || !engineSupported()) return;
    let made: Scene | null = null;
    let gone = false;
    const stop = new AbortController();
    // A fetch the network dropped is no missing WebGL: the code is fetched until it loads, and
    // only a scene that cannot start says the browser cannot draw.
    loadAgain(loadEngine, stop.signal).then(
      ({ createScene }) =>
        // A track keeps its last picture while its next one has no frame yet (spec cut-preload).
        createScene(first.current, { hold: true })
          .then((ready) => {
            if (gone) {
              ready.destroy();
              return;
            }
            made = ready;
            ready.canvas.setAttribute("aria-hidden", "true");
            element.append(ready.canvas);
            scene.current = ready;
            setState("ready");
          })
          .catch(() => {
            if (!gone) setState("unavailable");
          }),
      // Stopped: the preview went while its code was fetched.
      () => undefined,
    );
    return () => {
      gone = true;
      stop.abort();
      scene.current = null;
      made?.destroy();
    };
  }, [host]);
  const draw = useCallback(
    (project: EngineProject, atMs: number, pictures: Pictures): readonly Drawn[] =>
      scene.current?.draw(project, atMs, pictures) ?? [],
    [],
  );
  return { state, draw };
}

/** A sound clip heard at the playhead, at its level then, while the preview plays. */
function PreviewSound(props: {
  clipId: string;
  url: string;
  sourceMs: number;
  gain: number;
  clocked: boolean;
  register: (clipId: string, element: HTMLAudioElement | null) => void;
}) {
  const { clipId, register } = props;
  const element = useFollow<HTMLAudioElement>(props.url, props.sourceMs, props.clocked, {
    muted: false,
    volume: props.gain,
  });
  useEffect(() => {
    register(clipId, element.current);
    return () => register(clipId, null);
  }, [clipId, register, element]);
  // Read with CORS, as a video's is: a boost past 100 % routes it through Web Audio (spec
  // volume-boost), which plays a file read without CORS from another origin as silence. A sound
  // has no captions to give.
  // oxlint-disable-next-line jsx-a11y/media-has-caption
  return <audio ref={element} src={props.url} crossOrigin={mediaCrossOrigin(props.url)} preload="auto" hidden />;
}

interface LayerProps {
  clipId: string;
  url: string;
  register: (clipId: string, element: MediaElement | null) => void;
  onPicture: () => void;
}

/**
 * A video layer's element, kept at the playhead for the scene to read (or, before its clip shows,
 * paused at its first frame): out of sight but rendered (a browser may stop decoding a video that
 * is not), read cross-origin so WebGL may use it (the media bucket's CORS names the app's
 * origin). It plays its own sound unless it is muted, at its level then.
 */
function LayerVideo(props: LayerProps & { sourceMs: number; clocked: boolean; gain: number | null }) {
  const { clipId, url, sourceMs, clocked, gain, register, onPicture } = props;
  const video = useFollow<HTMLVideoElement>(url, sourceMs, clocked, {
    muted: gain === null,
    volume: gain ?? 0,
  });
  useEffect(() => {
    register(clipId, video.current);
    return () => register(clipId, null);
  }, [clipId, register, video]);
  return (
    // The user's own clips carry no caption track; the titles are DOM text a screen reader reads.
    // oxlint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={video}
      className={classes.engineMedia}
      src={url}
      crossOrigin={mediaCrossOrigin(url)}
      playsInline
      preload="auto"
      aria-hidden
      onLoadedData={onPicture}
      onSeeked={onPicture}
      // A file that fails has no frame to wait for: drawn again, its track holds nothing more.
      onError={onPicture}
    />
  );
}

/** A still layer's element, as a video's: rendered out of sight, read cross-origin. */
function LayerImage({ clipId, url, register, onPicture }: LayerProps) {
  const keep = useCallback(
    (element: HTMLImageElement | null) => {
      register(clipId, element);
      return () => register(clipId, null);
    },
    [clipId, register],
  );
  return (
    <img
      ref={keep}
      className={classes.engineMedia}
      src={url}
      alt=""
      crossOrigin={mediaCrossOrigin(url)}
      onLoad={onPicture}
      onError={onPicture}
    />
  );
}

/**
 * Live's text editor on the canvas (SPEC B5): a textarea over the text, where it was drawn, in
 * its face, size, alignment and colour, with a 2px sky border. Typing changes the text as it
 * goes; a blur or ⌘/Ctrl+Enter keeps it, Escape puts back the words it opened with.
 */
function TextEditor(props: {
  text: TextClip;
  box: Drawn;
  project: EngineProject;
  onChange: (text: string) => void;
  onClose: (keep: boolean) => void;
}) {
  const { text, box, project, onChange, onClose } = props;
  const { style } = text;
  const share = shareOf(box, project.size);
  // The frame's pixels as a share of the stage's width (the stage is a size container).
  const cqw = (framePx: number) => `${(framePx / project.size.width) * 100}cqw`;
  const pad = paddingOf(style);
  return (
    <textarea
      className={classes.textEditor}
      aria-label="Edit text"
      // The editor opens on a double-click and takes the keys at once, as live's does.
      // oxlint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={text.text}
      maxLength={textClipTextMax}
      spellCheck={false}
      style={{
        left: `${share.left * 100}%`,
        top: `${share.top * 100}%`,
        width: `${share.width * 100}%`,
        minHeight: `${share.height * 100}%`,
        transform: `rotate(${box.angle}deg)`,
        fontFamily: `"${faceName(style.font)}", sans-serif`,
        fontWeight: style.weight,
        fontStyle: style.italic ? "italic" : "normal",
        fontSize: cqw(style.size * project.unit),
        lineHeight: LINE_PITCH,
        textAlign: style.align,
        textTransform: style.textCase === "upper" ? "uppercase" : style.textCase === "lower" ? "lowercase" : "none",
        padding: `${cqw(pad.y * project.unit)} ${cqw(pad.x * project.unit)}`,
        ...(style.color === null ? {} : { color: style.color }),
      }}
      onFocus={(event) => {
        const end = event.currentTarget.value.length;
        event.currentTarget.setSelectionRange(end, end);
      }}
      onChange={(event) => onChange(event.currentTarget.value)}
      onBlur={() => onClose(true)}
      onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose(false);
        } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          onClose(true);
        }
      }}
    />
  );
}

/**
 * The video's preview, drawn by the engine (spec E2): every picture showing at the playhead,
 * bottom track first, and the titles over them, on a canvas at the project's frame (720 on its
 * short side), so the preview is the frame an export makes. While playing, a clock moves the
 * playhead and the media follow it, and each animation frame is drawn; paused, a frame is drawn
 * at the playhead once its pictures are there. Every sound plays at its level, fades included.
 * The titles at the playhead are also listed for a screen reader.
 */
export function Preview(props: PreviewProps) {
  const { timeline, aspectRatio, onAspect, media, playheadMs, playing, onPlayhead, onPlaying, onPick } = props;
  // Live's text editor: the text being edited, its words when it opened, and where it was drawn.
  const [editing, setEditing] = useState<{ id: string; original: string; box: Drawn } | null>(null);
  const editingId = editing?.id ?? null;
  // A drag on the canvas (issue 560): drawn as it goes from the timeline with its patches, and
  // kept as one step when it is let go.
  const [drag, setDrag] = useState<CanvasDrag | null>(null);
  const outcome = useMemo(
    () =>
      drag === null || !drag.moving
        ? null
        : dragOutcome(drag, timeline.clips, drag.others, drag.frame, drag.threshold),
    [drag, timeline.clips],
  );
  const shownTimeline = useMemo(
    () =>
      outcome === null
        ? timeline
        : outcome.patches.reduce(
            (shown, { id, patch }) => editTimeline(shown, { type: "setClip", id, patch }),
            timeline,
          ),
    [timeline, outcome],
  );
  // What the preview plays (spec proxy-playback.md): each library video's 540p copy, or the
  // originals when this browser chose them. Only the preview's project takes the copies: Download
  // and Save resolve the project from `media`, the originals.
  const [quality, setQuality] = useState<PreviewQuality>(() => readPref("previewQuality"));
  const chooseQuality = (next: PreviewQuality) => {
    writePref("previewQuality", next);
    setQuality(next);
  };
  const played = useMemo(() => playedMedia(media, quality), [media, quality]);
  const qualityShown = useMemo(() => qualityWords(media, quality), [media, quality]);
  const project = useMemo(() => {
    const resolved = resolveProject(shownTimeline, aspectRatio, played);
    // While its editor is open, the text is the editor's, not the frame's (as live hides it).
    return editingId === null
      ? resolved
      : { ...resolved, texts: resolved.texts.filter((text) => text.clipId !== editingId) };
  }, [shownTimeline, aspectRatio, played, editingId]);
  const total = project.durationMs;
  // At the end, the last frame stays on screen.
  const held = (ms: number) => (total > 0 && ms >= total ? total - 1 : ms);
  // Paused, the preview axis's time shows instead of the playhead's (spec capcut-shortcuts.md);
  // the files are still read ahead from the playhead, and Play still starts there.
  const shownMs = held(!playing && props.hoverMs !== null ? props.hoverMs : playheadMs);
  const readMs = held(playheadMs);
  const host = useRef<HTMLDivElement>(null);
  const { state, draw } = useScene(host, project.size);

  // The canvas's view (spec capcut-shortcuts.md): the stage scaled by CSS about the panel's middle
  // and moved by the pan; its fit is its laid-out width over the frame's, in live's pixels.
  const [view, setView] = useState<CanvasView>(FIT_VIEW);
  const stage = useRef<HTMLDivElement>(null);
  const [stageWidth, setStageWidth] = useState(0);
  useLayoutEffect(() => {
    const element = stage.current;
    if (element === null) return;
    const measure = () => setStageWidth(element.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const liveSize = liveFrame(aspectRatio);
  const fit = fitScale(stageWidth, liveSize.width);
  const scale = stageScale(view.zoom, fit);
  const area = useRef<HTMLElement>(null);
  const [fullScreen, setFullScreen] = useState(false);
  useEffect(() => {
    const changed = () => setFullScreen(area.current !== null && document.fullscreenElement === area.current);
    document.addEventListener("fullscreenchange", changed);
    return () => document.removeEventListener("fullscreenchange", changed);
  }, []);
  const toggleFullScreen = () => {
    const element = area.current;
    if (element === null || !fullScreenSupported()) return;
    const asked = document.fullscreenElement === element ? document.exitFullscreen() : element.requestFullscreen();
    // A refusal (no user gesture, a policy) leaves the canvas where it is.
    void asked.catch(() => undefined);
  };
  const zoomTo = (zoom: (current: CanvasView) => CanvasZoom) =>
    setView((current) => zoomed(current, zoom(current), fit));
  const viewCommands: Record<CanvasCommand, () => void> = {
    moveTool: () => setView((current) => ({ ...current, tool: "move" })),
    handTool: () => setView((current) => ({ ...current, tool: "hand" })),
    zoomToFit: () => zoomTo(() => "fit"),
    zoom50: () => zoomTo(() => 0.5),
    zoom100: () => zoomTo(() => 1),
    zoom200: () => zoomTo(() => 2),
    canvasZoomIn: () => zoomTo((current) => steppedZoom(current.zoom, fit, 1)),
    canvasZoomOut: () => zoomTo((current) => steppedZoom(current.zoom, fit, -1)),
    fullScreen: toggleFullScreen,
  };
  useImperativeHandle(props.controls, () => ({ run: (command) => viewCommands[command]() }));
  const [panning, setPanning] = useState(false);

  // What a frame is drawn from, read at draw time: the project, the time and the elements.
  const latest = useRef<{ project: EngineProject; shownMs: number; playing: boolean }>({
    project,
    shownMs,
    playing,
  });
  useEffect(() => {
    latest.current = { project, shownMs, playing };
  });
  const elements = useRef(new Map<string, MediaElement>());
  // Each playing video's frame ask now waiting (`stopWatching`).
  const waiting = useRef(new Map<HTMLVideoElement, number>());
  const register = useCallback((clipId: string, element: MediaElement | null) => {
    const gone = elements.current.get(clipId);
    if (gone instanceof HTMLVideoElement && gone !== element) stopWatching(waiting.current, gone);
    if (element === null) elements.current.delete(clipId);
    else elements.current.set(clipId, element);
  }, []);
  // The sound clips' elements, by clip: the play clock keeps them with the pictures'.
  const heard = useRef(new Map<string, HTMLAudioElement>());
  const registerSound = useCallback((clipId: string, element: HTMLAudioElement | null) => {
    if (element === null) heard.current.delete(clipId);
    else heard.current.set(clipId, element);
  }, []);
  // How this browser starts a video, learned from each start (`StartPace`): its time moves at once,
  // its pictures only once its sound's output has started, by then some way into its file.
  const pace = useRef<StartPace>(FIRST_PACE);
  /**
   * Playing, at every frame: each video and sound element plays at t's time of its file, or rests
   * (spec playback-trail). A clip starts and stops at the frame of its cut, not at the playhead's
   * next step; a video loaded ahead starts so its first picture is its in-point's at its cut; an
   * element off the clock closes the gap by its rate, or a seek when far off.
   */
  const follow = useCallback(
    (atMs: number, run: ClockRun) => {
      const { project } = latest.current;
      const soundOf = new Map(project.sounds.map((sound) => [sound.clipId, sound]));
      const now = (element: HTMLMediaElement) => {
        const currentMs = element.currentTime * 1000;
        const at = performance.now();
        // How long it has played with no progress: its time, its ready state and the end of what it
        // has of its file all still. One loading slowly makes some; paused, it starts again.
        const { buffered } = element;
        const progress = `${currentMs} ${element.readyState} ${buffered.length === 0 ? 0 : buffered.end(buffered.length - 1)}`;
        const last = run.progressed.get(element);
        const still = !element.paused && last !== undefined && last.progress === progress;
        if (!still) run.progressed.set(element, { progress, at });
        return {
          currentMs,
          paused: element.paused,
          rate: element.playbackRate,
          seeking: element.seeking,
          muted: element.muted,
          volume: element.volume,
          boost: boostOf(element),
          stalledMs: still ? at - last.at : 0,
          reloads: run.reloads.get(element) ?? 0,
        };
      };
      const make = (element: HTMLMediaElement, move: MediaMove) => {
        const fromMs = move.seekMs ?? element.currentTime * 1000;
        if (move.reload) run.reloads.set(element, (run.reloads.get(element) ?? 0) + 1);
        apply(element, move);
        if (!(element instanceof HTMLVideoElement) || !("requestVideoFrameCallback" in element)) return;
        // A start moved by a seek teaches nothing: its pictures jump.
        if (move.seekMs !== undefined) run.starts.delete(element);
        if (move.pause) stopWatching(waiting.current, element);
        if (move.play) {
          // A start after loading again waited for its file: it teaches nothing either.
          if (!move.reload) run.starts.set(element, { at: performance.now(), fromMs });
          run.watching = true;
        } else if (element.paused || run.watched.has(element)) {
          return;
        }
        // Its every picture asked for while it plays, from its start, or from now for one already
        // playing when this run began (the project's length changed as it played): only one-off
        // asks left the hidden video's pictures a frame late, and stalled them 200 ms a second
        // after the last (decision 4).
        const watch = Symbol("watch");
        run.watched.set(element, watch);
        const seen = (shownAt: number, frame: VideoFrameCallbackMetadata) => {
          waiting.current.delete(element);
          if (run.over || run.watched.get(element) !== watch || element.paused) return;
          const start = run.starts.get(element);
          const aheadMs = start === undefined ? 0 : frame.mediaTime * 1000 - start.fromMs;
          if (start !== undefined && aheadMs > 1) {
            pace.current = nextPace(pace.current, { leadMs: shownAt - start.at, aheadMs });
            run.first ??= { at: shownAt, aheadMs };
            run.starts.delete(element);
          }
          waiting.current.set(element, element.requestVideoFrameCallback(seen));
        };
        stopWatching(waiting.current, element);
        waiting.current.set(element, element.requestVideoFrameCallback(seen));
      };
      // Half a frame more: the start is asked at the first frame within reach of the cut.
      const reach = { ...pace.current, leadMs: pace.current.leadMs + 8 };
      for (const layer of project.layers) {
        const element = elements.current.get(layer.clipId);
        if (!(element instanceof HTMLVideoElement)) continue;
        make(element, moveToward(now(element), layerWant(layer, soundOf.get(layer.clipId), atMs, reach)));
      }
      for (const [clipId, element] of heard.current) {
        const sound = soundOf.get(clipId);
        if (sound !== undefined) make(element, moveToward(now(element), soundWant(sound, atMs)));
      }
    },
    [],
  );
  // What the last frame drew and where: a press picks from it, and the selection outlines it
  // (kept as state only when it changes, so playing does not render the page every frame).
  const [drawn, setDrawn] = useState<readonly Drawn[]>([]);
  const lastDrawn = useRef<readonly Drawn[]>([]);
  // Paused, a seek's exact frame decoded from the files read ahead (spec instant-scrub).
  const scrub = useRef<ScrubCache | null>(null);
  const paint = useCallback(
    (atMs: number) => {
      const pictures = new Map<string, Picture>();
      for (const [clipId, element] of elements.current) {
        const picture = pictureOf(element);
        if (picture !== null) pictures.set(clipId, picture);
      }
      // Paused, a video whose element is not at its frame yet (seeking, or a frame or so off) shows
      // that frame decoded, once it is; the element's own picture again once it is there.
      const { project: shownProject, playing: moving } = latest.current;
      if (!moving && scrub.current !== null) {
        const asks: ScrubAsk[] = [];
        for (const { layer, sourceMs } of layersAt(shownProject, atMs)) {
          if (layer.kind !== "video") continue;
          const element = elements.current.get(layer.clipId);
          const there =
            element instanceof HTMLVideoElement &&
            !element.seeking &&
            element.readyState >= 2 &&
            Math.abs(element.currentTime * 1000 - sourceMs) < 1;
          if (!there) asks.push({ clipId: layer.clipId, url: layer.url, ms: sourceMs });
        }
        for (const [clipId, decoded] of scrub.current.frames(asks)) pictures.set(clipId, decoded);
      }
      const now = draw(shownProject, atMs, pictures);
      if (!sameDrawn(now, lastDrawn.current)) {
        lastDrawn.current = now;
        setDrawn(now);
      }
      // Which time the canvas shows, for a test to wait on.
      host.current?.setAttribute("data-frame", String(Math.round(atMs)));
    },
    [draw],
  );
  // A picture arrived (loaded, or a seek landed): paused, the frame is drawn again with it.
  const onPicture = useCallback(() => {
    if (!latest.current.playing) paint(latest.current.shownMs);
  }, [paint]);
  // The scrub cache lives with the preview; it loads mediabunny when it is first asked.
  useEffect(() => {
    const cache = new ScrubCache(() => import("mediabunny"), onPicture);
    scrub.current = cache;
    return () => {
      scrub.current = null;
      cache.dispose();
    };
  }, [onPicture]);
  // Paused, the files are read ahead from the playhead; playing, they are not.
  useEffect(() => {
    if (playing) scrub.current?.rest();
    else scrub.current?.warm(project, readMs);
  }, [project, readMs, playing]);
  // A text waits for its face: once the faces are loaded, the paused frame is drawn again.
  const [faces, setFaces] = useState(0);
  useEffect(() => {
    const needed = facesOf(project);
    if (needed.every(faceReady)) return;
    let gone = false;
    void loadFaces(needed).then(() => {
      if (!gone) setFaces((count) => count + 1);
    });
    return () => {
      gone = true;
    };
  }, [project]);
  useEffect(() => {
    if (!playing && state === "ready") paint(shownMs);
  }, [playing, shownMs, project, state, paint, faces]);

  // The clock: playing, the playhead moves with the wall clock about 20 times a second (enough
  // for the eye, and the whole editor does not render on every frame), every animation frame is
  // drawn at its own time, and it stops at the end. It starts with the pictures: once the videos
  // asked to play show their first new picture, at that picture's time (spec playback-trail).
  const from = useRef(playheadMs);
  useEffect(() => {
    from.current = playheadMs;
  });
  useEffect(() => {
    if (!playing) return;
    if (total === 0) {
      onPlaying(false);
      return;
    }
    const pressed = performance.now();
    const origin = from.current >= total ? 0 : from.current;
    // This run's starts: a new run of the clock learns from none of an old one's.
    const run: ClockRun = {
      watched: new WeakMap(),
      starts: new WeakMap(),
      progressed: new WeakMap(),
      reloads: new WeakMap(),
      watching: false,
      first: null,
      over: false,
    };
    const asks = waiting.current;
    let began: number | null = null;
    let asked = false;
    let shown = 0;
    let frame = requestAnimationFrame(function tick(now) {
      if (began === null) {
        // Asked once and left to start: held to a clock that has not started, it would be slowed.
        if (!asked) follow(origin, run);
        asked = true;
        // Every video at the playhead asked to play waits for its first new picture (none to wait
        // for where the browser cannot tell): the clock starts there, at that picture's time.
        if (run.first !== null) began = run.first.at - run.first.aheadMs;
        else if (!run.watching || now - pressed >= MOST_FIRST_WAIT_MS) began = now;
        else {
          paint(origin);
          frame = requestAnimationFrame(tick);
          return;
        }
      }
      // A frame's `now` is when it began, which can be a little before `began`: never before the
      // playhead it starts from, where nothing shows before the first clip (spec cut-preload).
      const t = origin + Math.max(0, now - began);
      if (t >= total) {
        onPlayhead(total);
        onPlaying(false);
        return;
      }
      follow(t, run);
      paint(t);
      if (now - shown >= PLAYHEAD_STEP_MS) {
        shown = now;
        onPlayhead(t);
      }
      frame = requestAnimationFrame(tick);
    });
    return () => {
      run.over = true;
      cancelAnimationFrame(frame);
      for (const element of asks.keys()) stopWatching(asks, element);
    };
  }, [playing, total, onPlayhead, onPlaying, paint, follow]);

  const sounds = soundsAt(project, shownMs);
  const gainOf = new Map(sounds.map(({ sound, gain }) => [sound.clipId, gain]));
  // The pictures showing, and the ones to come in the next 2 s paused at their first frames, so a
  // cut finds its next picture decoded (spec cut-preload). One list in the project's order: a
  // picture goes from coming to showing in place, the same element, never moved or loaded again.
  const showing = new Map(layersAt(project, shownMs).map((each) => [each.layer.clipId, each]));
  const ahead = new Map(layersAhead(project, shownMs, AHEAD_MS).map((each) => [each.layer.clipId, each.sourceMs]));
  const mounted = project.layers.flatMap((layer) => {
    const now = showing.get(layer.clipId);
    // A transition holds a picture at its first or last frame (#240): paused there.
    if (now !== undefined) return [{ layer, sourceMs: now.sourceMs }];
    const first = ahead.get(layer.clipId);
    return first === undefined ? [] : [{ layer, sourceMs: first }];
  });
  const pictured = new Set(project.layers.map((layer) => layer.clipId));

  const chosen = props.selection?.ids ?? [];
  const outlined = editing === null ? drawn.filter((box) => chosen.includes(box.clipId)) : [];
  // Several chosen that may all change: live's joint box round them, as a share of the frame.
  const joint =
    outlined.length > 1 &&
    !props.readOnly &&
    outlined.every((box) => timeline.clips.find((each) => each.id === box.clipId)?.locked === false)
      ? jointOf(outlined.map((box) => placeOf(box, 1)))
      : null;
  const jointBox =
    joint === null
      ? null
      : {
          left: (joint.cx - joint.width / 2) / project.size.width,
          top: (joint.cy - joint.height / 2) / project.size.height,
          width: joint.width / project.size.width,
          height: joint.height / project.size.height,
        };
  /** The clip drawn on top where the pointer is, or none. */
  const pickAt = (event: { clientX: number; clientY: number }) => {
    const frame = host.current;
    if (frame === null) return null;
    const { x, y } = toFrame(frame.getBoundingClientRect(), project.size, event.clientX, event.clientY);
    return pick(lastDrawn.current, x, y);
  };
  /** A pointer's place on the frame in live's pixels, and the screen's pixels to one of live's. */
  const liveAt = (event: { clientX: number; clientY: number }): (Point & { scale: number }) | null => {
    const frame = host.current;
    if (frame === null) return null;
    const rect = frame.getBoundingClientRect();
    const { x, y } = toFrame(rect, project.size, event.clientX, event.clientY);
    const scale = (Math.max(1, rect.width) / project.size.width) * project.unit;
    return { x: x / project.unit, y: y / project.unit, scale };
  };
  /** Starts a drag of `ids` as they were last drawn, unless none of them may change. */
  const beginDrag = (gesture: Gesture, ids: readonly string[], event: { clientX: number; clientY: number }) => {
    const at = liveAt(event);
    if (at === null || props.readOnly) return;
    const starts = new Map<string, ReturnType<typeof placeOf>>();
    for (const id of ids) {
      const clip = timeline.clips.find((each) => each.id === id);
      const box = lastDrawn.current.find((each) => each.clipId === id);
      if (clip === undefined || clip.locked || box === undefined) continue;
      starts.set(id, placeOf(box, project.unit));
    }
    if (starts.size === 0) return;
    const others = lastDrawn.current
      .filter((box) => !starts.has(box.clipId))
      .map((box) => boundsOf(placeOf(box, project.unit)));
    const begun: CanvasDrag = {
      gesture,
      starts,
      from: at,
      to: at,
      frame: { width: project.size.width / project.unit, height: project.size.height / project.unit },
      others,
      threshold: SNAP_SCREEN_PX / at.scale,
      scale: at.scale,
      moving: gesture !== "move",
    };
    followDrag(begun);
  };
  // A press on the frame: the clip drawn on top there, or none. A press on the open editor is
  // the editor's own. A press on an item also starts its move (with the others chosen with it).
  const press = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    // The Hand tool's press chooses nothing: it pans the view.
    if (view.tool === "hand") {
      panFrom(event);
      return;
    }
    if (timeline.clips.length === 0 || editing !== null) return;
    const picked = pickAt(event);
    onPick(picked, event.shiftKey);
    if (picked === null || event.shiftKey) return;
    beginDrag("move", chosen.includes(picked) ? chosen : [picked], event);
  };
  // The drag follows the pointer anywhere on the page, and is one step when let go. The page's
  // listeners start with the press itself (a quick flick sends its moves and its release before
  // any render), keep the drag in a ref as it goes, and read the render's latest values here.
  const latestDrag = useRef({ liveAt, onEdit: props.onEdit, clips: timeline.clips });
  useEffect(() => {
    latestDrag.current = { liveAt, onEdit: props.onEdit, clips: timeline.clips };
  });
  const stopDrag = useRef<(() => void) | null>(null);
  useEffect(() => () => stopDrag.current?.(), []);
  const followDrag = (begun: CanvasDrag) => {
    stopDrag.current?.();
    let current = begun;
    setDrag(current);
    const follow = (event: globalThis.PointerEvent) => {
      const at = latestDrag.current.liveAt(event);
      if (at === null) return;
      const travel = Math.hypot(at.x - current.from.x, at.y - current.from.y) * current.scale;
      current = { ...current, to: { x: at.x, y: at.y }, moving: current.moving || travel >= MOVE_START_PX };
      setDrag(current);
    };
    const finish = (event: globalThis.PointerEvent) => {
      follow(event);
      stop();
      if (!current.moving) return;
      const { patches } = dragOutcome(current, latestDrag.current.clips, current.others, current.frame, current.threshold);
      const edits = patches.map(({ id, patch }): TimelineEdit => ({ type: "setClip", id, patch }));
      const [first] = edits;
      if (first !== undefined) latestDrag.current.onEdit(edits.length === 1 ? first : { type: "many", edits });
    };
    const release = holdSelection();
    const stop = () => {
      release();
      window.removeEventListener("pointermove", follow);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", stop);
      stopDrag.current = null;
      setDrag(null);
    };
    window.addEventListener("pointermove", follow);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", stop);
    stopDrag.current = stop;
  };
  /**
   * The Hand tool's drag: the view follows the pointer anywhere on the page, held so the panel's
   * middle stays on the frame (`panned`), until it is let go.
   */
  const panFrom = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    stopDrag.current?.();
    let last = { x: event.clientX, y: event.clientY };
    const box = { width: stageWidth, height: (stageWidth * liveSize.height) / liveSize.width };
    setPanning(true);
    const follow = (moved: globalThis.PointerEvent) => {
      const dx = moved.clientX - last.x;
      const dy = moved.clientY - last.y;
      last = { x: moved.clientX, y: moved.clientY };
      setView((current) => panned(current, dx, dy, box, fit));
    };
    const stop = () => {
      window.removeEventListener("pointermove", follow);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      stopDrag.current = null;
      setPanning(false);
    };
    window.addEventListener("pointermove", follow);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    stopDrag.current = stop;
  };
  // Live's double-click on a text opens its editor over it, unless it may not change (or the Hand
  // tool is on).
  const openEditor = (event: MouseEvent<HTMLDivElement>) => {
    if (props.readOnly || editing !== null || view.tool === "hand") return;
    const id = pickAt(event);
    const box = lastDrawn.current.find((each) => each.clipId === id && each.kind === "text");
    const clip = timeline.clips.find((each) => each.id === id);
    if (box === undefined || clip?.kind !== "text" || clip.locked) return;
    onPick(clip.id, false);
    setEditing({ id: clip.id, original: clip.text, box });
  };
  // Live's right-click on the canvas: its menu, on the clip drawn on top there (selected) or none
  // (Paste, there even before the first clip).
  const openMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (editing !== null) return;
    event.preventDefault();
    props.onMenu({ x: event.clientX, y: event.clientY, clipId: pickAt(event) });
  };
  // A phone's long-press is that right-click: the press's move stops, and the menu opens there.
  // A text's editor open on the frame keeps a finger's own long-press (its selection, Copy and
  // Paste), so the hook is off while it is, as the right-click's menu is.
  useCanvasTouch(
    stage,
    props.touch === true && editing === null
      ? {
          onHold: () => stopDrag.current?.(),
          onMenu: (x, y) => props.onMenu({ x, y, clipId: pickAt({ clientX: x, clientY: y }) }),
        }
      : undefined,
  );
  // Live's canvas moves: with the canvas focused, the arrows move the chosen pictures and texts
  // 1 of live's pixels, 5 with Shift, as one step. A picture placed by its fit takes the box it
  // is drawn in.
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[
      event.key
    ];
    if (step === undefined || event.target !== event.currentTarget || props.readOnly) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const by = event.shiftKey ? 5 : 1;
    const [dx = 0, dy = 0] = step.map((each) => each * by);
    const unit = project.unit;
    const edits: TimelineEdit[] = [];
    for (const id of chosen) {
      const clip = timeline.clips.find((each) => each.id === id);
      const box = lastDrawn.current.find((each) => each.clipId === id);
      if (clip === undefined || clip.kind === "audio" || clip.kind === "effect" || clip.locked || box === undefined) {
        continue;
      }
      if (clip.kind === "text") {
        edits.push({ type: "setClip", id, patch: { style: { x: clip.style.x + dx, y: clip.style.y + dy } } });
        continue;
      }
      const place = clip.box ?? {
        x: (box.cx - box.width / 2) / unit,
        y: (box.cy - box.height / 2) / unit,
        width: box.width / unit,
        height: box.height / unit,
      };
      edits.push({ type: "setClip", id, patch: { box: { ...place, x: place.x + dx, y: place.y + dy } } });
    }
    const [first] = edits;
    if (first === undefined) return;
    event.preventDefault();
    props.onEdit(edits.length === 1 ? first : { type: "many", edits });
  };
  const editedClip = timeline.clips.find((each) => each.id === editingId);
  const edited = editedClip?.kind === "text" ? editedClip : undefined;

  return (
    <section ref={area} className={classes.previewArea} aria-label="Canvas" data-full-screen={fullScreen || undefined}>
      <div className={classes.canvasTools}>
        <AspectControl aspectRatio={aspectRatio} onAspect={onAspect} />
      </div>
      {qualityShown === null ? null : (
        <div className={classes.qualityTools}>
          <QualityPill words={qualityShown} quality={quality} onQuality={chooseQuality} />
        </div>
      )}
      {/* The frame is a drawing surface: it takes the focus and its own keys (the arrows move
          the selected clip, as live's canvas does), and its texts are listed for a screen reader
          below; a clip is also chosen, moved and changed from the timeline and its Parameters. */}
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={stage}
        className={classes.stage}
        style={{
          aspectRatio: cssRatio(aspectRatio),
          ["--frame-ratio" as string]: ratioOf(aspectRatio),
          ...(scale === 1 && view.pan.x === 0 && view.pan.y === 0
            ? {}
            : { transform: `translate(${view.pan.x}px, ${view.pan.y}px) scale(${scale})` }),
        }}
        data-ratio={aspectRatio}
        data-tool={view.tool}
        data-panning={panning || undefined}
        onPointerDown={press}
        onDoubleClick={openEditor}
        onContextMenu={openMenu}
        onKeyDown={move}
        role="application"
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        aria-label="Frame"
      >
        {editing === null || edited === undefined ? null : (
          <TextEditor
            text={edited}
            box={editing.box}
            project={project}
            onChange={(text) => props.onEdit({ type: "setClip", id: edited.id, patch: { text } })}
            onClose={(keep) => {
              if (!keep) props.onEdit({ type: "setClip", id: edited.id, patch: { text: editing.original } });
              setEditing(null);
            }}
          />
        )}
        {jointBox === null ? null : (
          // Live's transformer round several chosen items: their joint box, upright, with all
          // eight handles and the turn zones (none with the Hand tool, whose press pans); each item
          // keeps its own outline inside it.
          <div
            className={classes.selectionBox}
            data-selection-joint
            style={{
              left: `${jointBox.left * 100}%`,
              top: `${jointBox.top * 100}%`,
              width: `${jointBox.width * 100}%`,
              height: `${jointBox.height * 100}%`,
            }}
            aria-hidden
          >
            {view.tool === "hand" ? null : (
              <CanvasHandles
                handles={GROUP_HANDLES}
                turns
                onBegin={(gesture, event) => beginDrag(gesture, outlined.map((box) => box.clipId), event)}
              />
            )}
          </div>
        )}
        {outlined.map((box) => {
          const outline = shareOf(box, project.size);
          const clip = timeline.clips.find((each) => each.id === box.clipId);
          // Live's handles on the one chosen item that may change; a locked one is outlined only,
          // and so is any with the Hand tool, whose press pans the view (spec capcut-shortcuts.md).
          const handled =
            view.tool === "move" && outlined.length === 1 && !props.readOnly && clip !== undefined && !clip.locked;
          return (
            <div
              key={box.clipId}
              className={classes.selectionBox}
              data-selection={box.clipId}
              style={{
                left: `${outline.left * 100}%`,
                top: `${outline.top * 100}%`,
                width: `${outline.width * 100}%`,
                height: `${outline.height * 100}%`,
                transform: `rotate(${box.angle}deg)`,
              }}
              aria-hidden
            >
              {handled ? (
                <CanvasHandles
                  handles={handlesOf(box.kind)}
                  turns
                  onBegin={(gesture, event) => beginDrag(gesture, [box.clipId], event)}
                />
              ) : clip?.locked === true || jointBox !== null ? null : (
                CORNERS.map((corner) => (
                  <span key={corner} className={classes.corner} style={CORNER_PLACE[corner]} />
                ))
              )}
            </div>
          );
        })}
        {(outcome?.guides ?? []).map((guide, at) => {
          const frame = drag?.frame ?? { width: 1, height: 1 };
          const vertical = guide.type === "vertical";
          const across = vertical ? frame.width : frame.height;
          const along = vertical ? frame.height : frame.width;
          const style: CSSProperties = vertical
            ? {
                left: `${(guide.position / across) * 100}%`,
                top: `${(guide.start / along) * 100}%`,
                height: `${((guide.end - guide.start) / along) * 100}%`,
              }
            : {
                top: `${(guide.position / across) * 100}%`,
                left: `${(guide.start / along) * 100}%`,
                width: `${((guide.end - guide.start) / along) * 100}%`,
              };
          return (
            <span key={at} className={handleClasses.guide} data-type={guide.type} style={style} aria-hidden />
          );
        })}
        {/* The scene's canvas goes here. Pixels only: the titles at the playhead are listed for
            a screen reader below. */}
        <div
          ref={host}
          className={classes.engineFrame}
          data-engine={state}
          hidden={timeline.clips.length === 0 || state === "unavailable"}
        />
        {timeline.clips.length === 0 ? (
          <Text size="sm" className={classes.stageEmpty}>
            Add a video from the media panel to start.
          </Text>
        ) : state === "unavailable" ? (
          <Text size="sm" className={classes.stageEmpty}>
            This browser cannot draw the preview: it needs WebGL, which is off or missing here.
          </Text>
        ) : null}
        <VisuallyHidden>
          {textsAt(project, shownMs).map((text) => (
            <p key={text.clipId}>{text.text}</p>
          ))}
        </VisuallyHidden>
        {mounted.map(({ layer, sourceMs }) =>
          layer.kind === "video" ? (
            <LayerVideo
              key={layer.clipId}
              clipId={layer.clipId}
              url={layer.url}
              sourceMs={sourceMs}
              clocked={playing}
              gain={gainOf.get(layer.clipId) ?? null}
              register={register}
              onPicture={onPicture}
            />
          ) : (
            <LayerImage
              key={layer.clipId}
              clipId={layer.clipId}
              url={layer.url}
              register={register}
              onPicture={onPicture}
            />
          ),
        )}
        {sounds
          .filter(({ sound }) => !pictured.has(sound.clipId))
          .map(({ sound, sourceMs, gain }) => (
            <PreviewSound
              key={sound.clipId}
              clipId={sound.clipId}
              url={sound.url}
              sourceMs={sourceMs}
              gain={gain}
              clocked={playing}
              register={registerSound}
            />
          ))}
      </div>
      <CanvasTools
        view={view}
        mac={props.mac}
        canFullScreen={fullScreenSupported()}
        onCommand={(command) => viewCommands[command]()}
      />
      {fullScreen ? (
        // Full screen: the frame, and a bar to play it and to leave (Esc leaves too).
        <div className={classes.fullScreenBar} role="toolbar" aria-label="Full screen">
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={playing ? "Pause" : "Play"}
            disabled={timeline.clips.length === 0}
            onClick={() => onPlaying(!playing)}
          >
            {playing ? <Pause size={16} /> : <Play size={16} />}
          </ActionIcon>
          <span className={classes.fullScreenClock}>
            {formatTimecode(playheadMs, true)} | {formatTimecode(total)}
          </span>
          <ActionIcon variant="subtle" color="gray" aria-label="Exit full screen" onClick={toggleFullScreen}>
            <Minimize size={16} />
          </ActionIcon>
        </div>
      ) : null}
    </section>
  );
}
